import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { tryNormalizeUrl, type BidTarget, type BulkTargetResult } from '@bidmanager/shared';
import { errorCode, errorMessage, request } from '../api';

/**
 * Bid target list (§2.3).
 *
 * Only the link is required. Duplicate detection runs against the *normalized*
 * form, so the same posting pasted with a tracking parameter, a `www.`, or a
 * trailing slash is recognised as already present.
 */
export function Targets(): JSX.Element {
  const { profileId } = useParams<{ profileId: string }>();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [bulk, setBulk] = useState('');
  const [results, setResults] = useState<BulkTargetResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['targets', profileId],
    queryFn: () => request<BidTarget[]>('GET', `/profiles/${profileId}/targets`),
    enabled: Boolean(profileId),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['targets', profileId] });
    void queryClient.invalidateQueries({ queryKey: ['profiles'] });
  };

  const addOne = useMutation({
    mutationFn: () => request<BidTarget>('POST', `/profiles/${profileId}/targets`, { url }),
    onSuccess: () => {
      setUrl('');
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(
        errorCode(caught) === 'DUPLICATE_TARGET'
          ? 'That link is already on this list.'
          : errorMessage(caught),
      ),
  });

  const addMany = useMutation({
    mutationFn: () =>
      request<BulkTargetResult[]>('POST', `/profiles/${profileId}/targets/bulk`, {
        urls: bulk.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
      }),
    onSuccess: (outcome) => {
      setResults(outcome);
      setBulk('');
      refresh();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `/targets/${id}`),
    onSuccess: refresh,
    onError: (caught) => setError(errorMessage(caught)),
  });

  // Live feedback before a round trip — the same normalizer the server uses.
  const preview = url.trim() ? tryNormalizeUrl(url) : null;
  const alreadyListed =
    preview?.ok === true && data?.some((target) => target.urlNormalized === preview.value);

  return (
    <>
      <header className="page-head">
        <Link to="/profiles" className="small">
          ← Profiles
        </Link>
        <h1 style={{ marginTop: 8 }}>Bid targets</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Links your bidders should apply to. Duplicates are rejected automatically.
        </p>
      </header>

      <div className="stack">
        <section className="card">
          <div className="field">
            <label>Add a link</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={url}
                placeholder="https://jobs.example.com/postings/123"
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && preview?.ok && !alreadyListed) addOne.mutate();
                }}
              />
              <button
                className="primary"
                onClick={() => addOne.mutate()}
                disabled={!preview?.ok || alreadyListed || addOne.isPending}
              >
                Add
              </button>
            </div>
            {preview && !preview.ok && (
              <p className="error small" style={{ margin: '6px 0 0' }}>
                That does not look like a job link ({preview.reason}).
              </p>
            )}
            {alreadyListed && (
              <p className="warn small" style={{ margin: '6px 0 0' }}>
                Already on this list.
              </p>
            )}
            {preview?.ok && !alreadyListed && preview.value !== url.trim() && (
              // Shown because the stored form differs from what was pasted, and
              // a client seeing a "duplicate" later should understand why.
              <p className="muted small" style={{ margin: '6px 0 0' }}>
                Will be stored as {preview.value}
              </p>
            )}
          </div>

          <details>
            <summary className="muted small" style={{ cursor: 'pointer' }}>
              Paste many links
            </summary>
            <div className="field" style={{ marginTop: 10 }}>
              <textarea
                value={bulk}
                placeholder={'One link per line'}
                onChange={(e) => setBulk(e.target.value)}
              />
              <button
                className="sm"
                style={{ marginTop: 8 }}
                onClick={() => addMany.mutate()}
                disabled={!bulk.trim() || addMany.isPending}
              >
                Add all
              </button>
            </div>

            {results && (
              <div className="small" style={{ marginTop: 8 }}>
                {/* Per-row outcomes: a batch with one duplicate in it should not
                    fail the other twenty-nine. */}
                {(['created', 'duplicate', 'invalid'] as const).map((outcome) => {
                  const count = results.filter((r) => r.outcome === outcome).length;
                  if (count === 0) return null;
                  const tone =
                    outcome === 'created' ? 'ok' : outcome === 'duplicate' ? 'muted' : 'error';
                  return (
                    <div key={outcome} className={tone}>
                      {count} {outcome}
                    </div>
                  );
                })}
              </div>
            )}
          </details>
        </section>

        {error && <p className="error small">{error}</p>}

        <section className="card">
          {isLoading && <p className="muted">Loading…</p>}
          {data?.length === 0 && <p className="muted small">No targets yet.</p>}

          {data && data.length > 0 && (
            <div className="row" style={{ marginBottom: 8 }}>
              <strong>Targets</strong>
              {/* Same progress framing as the bidder's overlay, so both sides
                  of the relationship read the list the same way. */}
              <span className="muted small">
                {data.filter((t) => t.status === 'COMPLETED').length}/{data.length} done
              </span>
            </div>
          )}

          {data && data.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Link</th>
                  <th>Status</th>
                  <th>Bid by</th>
                  <th>Added</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((target) => (
                  <tr key={target.id}>
                    <td className="truncate">
                      <a href={target.url} target="_blank" rel="noreferrer">
                        {target.url}
                      </a>
                    </td>
                    <td>
                      {target.status === 'COMPLETED' ? (
                        <span className="badge ok">Bid placed</span>
                      ) : (
                        <span className="badge muted">Open</span>
                      )}
                    </td>
                    <td className="muted">
                      {target.completedByBidderName ?? '—'}
                      {target.completedAt && (
                        <div className="small">
                          {new Date(target.completedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="muted">{new Date(target.createdAt).toLocaleDateString()}</td>
                    <td>
                      {target.bidId ? (
                        <Link to={`/bids/${target.bidId}`}>View bid</Link>
                      ) : (
                        <button
                          className="ghost sm danger"
                          onClick={() => remove.mutate(target.id)}
                          disabled={remove.isPending}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}
